import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BookSearchPreview from './BookSearchPreview'
import { useBookSearch, BookSearchResult } from '../hooks/useBookSearch'
import Modal from './Modal'

interface AddClubSuggestionProps {
  clubId: number
  onClose: () => void
  onSuccess: () => void
}

export default function AddClubSuggestion({ clubId, onClose, onSuccess }: AddClubSuggestionProps) {
  const { searchQuery, setSearchQuery, results, loading, error: searchError } = useBookSearch()
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [previewBook, setPreviewBook] = useState<BookSearchResult | null>(null)
  const [selectedBook, setSelectedBook] = useState<BookSearchResult | null>(null)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  useEffect(() => {
    if (searchError) {
      setError(searchError)
    }
  }, [searchError])

  const handleSelectBook = (book: BookSearchResult) => {
    setSelectedBook(book)
    setSearchQuery('')
  }

  const handleSubmit = async () => {
    if (!selectedBook) {
      setError('Please select a book')
      return
    }

    setAdding(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/club_book_suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          suggestion: {
            notes: notes,
            book_attributes: {
              title: selectedBook.title,
              author: selectedBook.author,
              isbn: selectedBook.isbn,
              description: selectedBook.description,
              cover_url: selectedBook.cover_url,
              first_publish_year: selectedBook.first_publish_year,
              number_of_pages: selectedBook.number_of_pages,
              metadata: selectedBook.metadata
            }
          }
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to suggest book')
      }
    } catch (err) {
      setError('Failed to suggest book')
    } finally {
      setAdding(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suggest a Book</h2>
            <p className="text-gray-600">Suggest a book for the club to read</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {!selectedBook ? (
            <>
              {/* Search Input */}
              <div className="mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )}

              {/* Search Results */}
              {!loading && results.length > 0 && (
                <div className="space-y-3">
                  {results.map((book, index) => (
                    <button
                      key={index}
                      onClick={() => setPreviewBook(book)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full bg-white border-2 rounded-xl p-4 transition-all text-left ${
                        highlightedIndex === index
                          ? 'border-indigo-500 shadow-md bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        {book.cover_url ? (
                          <img
                            src={book.cover_url}
                            alt={book.title}
                            className="w-16 h-24 object-cover rounded shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            {book.first_publish_year && <span>{book.first_publish_year}</span>}
                            {book.number_of_pages && (
                              <>
                                {book.first_publish_year && <span>•</span>}
                                <span>{book.number_of_pages} pages</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!loading && searchQuery.length >= 2 && results.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-600">No books found</p>
                  <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
                </div>
              )}

              {!loading && searchQuery.length < 2 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-gray-600">Start typing to search for books</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Selected Book & Notes */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex gap-4">
                    {selectedBook.cover_url ? (
                      <img
                        src={selectedBook.cover_url}
                        alt={selectedBook.title}
                        className="w-20 h-30 object-cover rounded shadow-sm"
                      />
                    ) : (
                      <div className="w-20 h-30 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{selectedBook.title}</h3>
                      <p className="text-sm text-gray-600">by {selectedBook.author}</p>
                    </div>
                    <button
                      onClick={() => setSelectedBook(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Why do you suggest this book? (Optional)
                  </label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about why the club should read this book..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none resize-none"
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={adding}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {adding ? 'Suggesting...' : 'Suggest Book'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => {
            handleSelectBook(previewBook)
            setPreviewBook(null)
          }}
          showSelectButton={true}
        />
      )}
    </>
  )
}
